import { useState, useEffect, useRef } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { ensureNamespaces, areNamespacesCached } from '@i18n/loader'

/**
 * Hook to ensure required namespaces are loaded for a screen/page
 *
 * IMPORTANT: Only use at screen/page level, NOT in child components.
 * - Screen loads namespaces once → all children have translations available
 * - Child components should only consume via useLanguage(), never load
 *
 * @param {string[]} namespaces - Required namespaces for this screen
 * @returns {{ isLoading: boolean, error: Error | null }}
 */
export function useNamespaces (namespaces) {
  const { lang, updateTranslations } = useLanguage()

  // Stable sorted key for comparisons
  const sortedNamespaces = [...namespaces].sort()
  const namespacesKey = sortedNamespaces.join(',')

  const [isLoading, setIsLoading] = useState(() => {
    if (!lang) return true
    return !areNamespacesCached(lang, sortedNamespaces)
  })
  const [error, setError] = useState(null)

  // Track current request to handle race conditions
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!lang || sortedNamespaces.length === 0) return

    // Check if all namespaces are already cached
    if (areNamespacesCached(lang, sortedNamespaces)) {
      setIsLoading(false)
      return
    }

    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current

    setIsLoading(true)
    setError(null)
    ensureNamespaces(lang, sortedNamespaces)
      .then(data => {
        // Ignore stale responses
        if (requestIdRef.current !== currentRequestId) return

        updateTranslations(data)
        setIsLoading(false)
      })
      .catch(err => {
        // Ignore stale errors
        if (requestIdRef.current !== currentRequestId) return
        console.error('[useNamespaces] Load failed:', err)
        setError(err)
        setIsLoading(false)
      })
  }, [lang, namespacesKey, updateTranslations])
  return { isLoading, error }
}

export default useNamespaces
